import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as invoiceService from '../services/invoiceService';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import useStoreInfo from '../../../hooks/useCompanyInfo';
import {
  Box, Button, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Divider, Chip, Container, Alert, AlertTitle,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, FormControlLabel, Switch, CircularProgress, IconButton, Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import PaymentIcon from '@mui/icons-material/Payment';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from "react-i18next";
import { getPaymentStatus } from '../utils/invoiceCalculations';
import { generateInvoicePDFComplete, renderInvoiceTemplate } from '../utils/invoicePdfUtils';
import { PAYMENT_STATUS } from '../../../constants/paymentStatus';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import AddCustomerDialog from '../../customers/components/AddCustomerDialog';
import InvoicePreviewDialog from '../../../components/InvoicePreviewDialog';

const InvoiceViewPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // ✅ Get store info from Redux store (auto-fetches if needed)
  const { storeInfo } = useStoreInfo();
  
  // ✅ Get payment methods from Redux store
  const { paymentMethods: paymentMethodOptions, loading: paymentMethodsLoading } = useSelector(
    (state) => state.paymentMethod
  );
  
  // Payment dialog state
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [isFullPayment, setIsFullPayment] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Peppol dialog state
  const [openPeppolDialog, setOpenPeppolDialog] = useState(false);
  const [peppolSendOnPeppol, setPeppolSendOnPeppol] = useState(false);
  const [peppolAttachPdf, setPeppolAttachPdf] = useState(false);
  const [sendingToPeppol, setSendingToPeppol] = useState(false);
  const [peppolPreviewHtml, setPeppolPreviewHtml] = useState('');
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Email dialog state - UI visible but functionality disabled
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [loadingEmailPreview, setLoadingEmailPreview] = useState(false);
  
  // PDF preview loading state
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewHtml, setPdfPreviewHtml] = useState('');

  // General messages state (for errors, warnings, info)
  const [generalMessage, setGeneralMessage] = useState(null); // { type: 'error'|'warning'|'info'|'success', text: 'message' }

  // Edit customer dialog state
  const [openEditCustomerDialog, setOpenEditCustomerDialog] = useState(false);

  // Aggregate payments by payment method (distinct) - MUST be before early returns
  const aggregatedPayments = React.useMemo(() => {
    if (!invoice || !invoice.Payments || invoice.Payments.length === 0) return [];
    
    const paymentMap = {};
    invoice.Payments.forEach((payment) => {
      const methodName = payment.PaymentMethodName || payment.PaymentMethod?.Name || 'Unknown Method';
      const amount = Number(payment.Amount || 0);
      
      if (paymentMap[methodName]) {
        paymentMap[methodName] += amount;
      } else {
        paymentMap[methodName] = amount;
      }
    });
    
    return Object.entries(paymentMap).map(([methodName, totalAmount]) => ({
      methodName,
      totalAmount
    }));
  }, [invoice]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const invoiceData = await invoiceService.getInvoiceById(id);
        if (!cancelled) setInvoice(invoiceData);
      } catch (error) {
        if (!cancelled) setInvoice(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!paymentMethodOptions || paymentMethodOptions.length === 0) {
      dispatch(fetchPaymentMethods());
    }
  }, [dispatch]);

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography>{t('loading')}</Typography>
    </Container>
  );

  if (!invoice) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Alert severity="error">
        <AlertTitle>{t('invoice_view_not_found_title')}</AlertTitle>
        {t('invoice_view_not_found_message')}
      </Alert>
      <Button onClick={() => navigate('/invoices')} sx={{ mt: 2 }}>
        {t('invoice_view_back_to_invoices')}
      </Button>
    </Container>
  );

  // Extract customer data
  const customer = invoice.Customer || {};
  const customerName = customer.Name || invoice.CustomerName || 'N/A';
  const customerVAT = customer.VATNumber || invoice.CustomerVAT || 'N/A';
  const customerEmail = customer.Email || invoice.CustomerEmail || 'N/A';
  const customerPhone = customer.PhoneNumber || customer.MobilePhone || invoice.CustomerPhone || 'N/A';
  const customerAddress = customer.Street || invoice.CustomerAddress || 'N/A';
  const customerCity = customer.City || invoice.CustomerCity || '';
  const customerPostCode = customer.PostCode || '';
  const customerCountry = customer.Country || '';

  // ✅ Use stored invoice totals to avoid rounding issues from recalculation
  const lineItems = invoice.LineItems || [];
  const vatPercentage = Number(invoice.VATPercentage ?? 21);
  
  // Use stored invoice values (already calculated correctly when invoice was created)
  const totalIncl = Number(invoice.InvoiceTotalAmount || 0);
  const paidAmount = Number(invoice.InvoicePaidAmount || 0);
  const dueAmount = Number(invoice.InvoiceDueAmount ?? (totalIncl - paidAmount));
  
  // Use stored subtotal and VAT values from backend
  const totalExcl = Number(invoice.InvoiceSubTotal || 0);
  const totalVAT = Number(invoice.InvoiceTotalVATAmount || 0);
  const discountAmount = Number(invoice.InvoiceDiscount || 0);
  
  // Extract discount type and value
  const discountType = invoice.DiscountType || '';
  const discountPercentage = Number(invoice.DiscountPercentage || 0);
  const discountValue = discountType.toLowerCase() === 'percentage' 
    ? discountPercentage 
    : Number(invoice.DiscountAmount || 0);

  // Payment status
  const { label: paymentStatus, color: paymentColor } = getPaymentStatus(paidAmount, totalIncl);

  // InvoiceStatus is always 'Draft' or 'Sent'; PeppolStatus is always 'Not Sent', 'Sent', or 'Failed'
  const isDraft = invoice.InvoiceStatus?.trim() === 'Draft';
  const isPosted = invoice.InvoiceStatus?.trim() === 'Sent';
  const peppolStatus = (invoice.PeppolStatus || '').trim();

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const invoiceDate = lineItems.length > 0 && lineItems[0].CreatedDate 
    ? lineItems[0].CreatedDate 
    : invoice.InvoiceDate || invoice.CreatedDate;

  const handleEdit = () => {
    navigate(`/invoices/edit/${invoice.Id}`);
  };

  const handleDelete = () => {
    // Block deletion if invoice was successfully sent to Peppol (not just attempted)
    const peppolLocked = invoice.IsSendOnPeppol && peppolStatus.toLowerCase() === 'sent';
    if (peppolLocked) {
      setGeneralMessage({ type: 'error', text: t('invoice_delete_peppol_error') || 'Cannot delete an invoice that has been sent to Peppol.' });
      return;
    }

    // Open custom confirmation dialog
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteDialogOpen(false);
    
    try {
      await invoiceService.deleteInvoice(invoice.Id);
      setGeneralMessage({ type: 'success', text: t('invoice_delete_success') || 'Invoice deleted successfully.' });
      setTimeout(() => navigate('/invoices'), 1500);
    } catch (error) {
      setGeneralMessage({ type: 'error', text: t('invoice_delete_failed') || 'Failed to delete invoice.' });
    }
  };

  const handleSendToPeppol = async () => {
    // Validate Peppol ID
    if (!customer.CustomerPeppolId) {
      setGeneralMessage({ type: 'error', text: t('peppol_id_missing_message') || 'Peppol ID is missing for this customer. Please add it by editing this customer.' });
      return;
    }

    // Validate email and phone
    const missingFields = [];
    if (!customer.Email) missingFields.push('email address');
    if (!customer.PhoneNumber) missingFields.push('phone number');
    
    if (missingFields.length > 0) {
      const fieldsList = missingFields.join(' and ');
      setGeneralMessage({ 
        type: 'warning', 
        text: t('peppol_missing_contact_info') || `This customer does not have ${fieldsList}. Please add them by editing customer to send this invoice to Peppol.` 
      });
      return;
    }

    // Generate preview
    setLoadingPreview(true);
    try {
      if (storeInfo) {
        const previewHtml = await renderInvoiceTemplate(invoice, storeInfo, t);
        setPeppolPreviewHtml(previewHtml);
      }
    } catch (error) {
      setGeneralMessage({ type: 'error', text: t('invoice_view_preview_failed') });
    } finally {
      setLoadingPreview(false);
    }

    // Open dialog for confirmation
    setPeppolSendOnPeppol(true);
    setPeppolAttachPdf(true);
    setOpenPeppolDialog(true);
  };

  const handleClosePeppolDialog = () => {
    setOpenPeppolDialog(false);
    setPeppolSendOnPeppol(false);
    setPeppolAttachPdf(false);
    setPeppolPreviewHtml('');
  };

  const handleConfirmSendToPeppol = async () => {
    if (!peppolSendOnPeppol) {
      setGeneralMessage({ type: 'error', text: t('please_confirm_send_to_peppol') || 'Please confirm sending to Peppol' });
      return;
    }

    try {
      setSendingToPeppol(true);
      setOpenPeppolDialog(false);
      setLoading(true);
      
      // Create FormData for the request
      const formData = new FormData();
      formData.append('InvoiceId', invoice.Id);
      formData.append('IncludeAttachments', peppolAttachPdf);
      
      // If attach PDF is enabled, generate and include PDF
      if (peppolAttachPdf) {
        if (!storeInfo) {
        setGeneralMessage({ type: 'error', text: t('invoice_view_store_not_loaded') });
        }

        // Generate PDF using the utility service
        const pdfBlob = await generateInvoicePDFComplete(invoice, storeInfo, t);
        formData.append('PdfAttachment', pdfBlob, `${invoice.InvoiceNumber}.pdf`);
      }

      await invoiceService.sendInvoiceToPeppol(formData);
      
      setGeneralMessage({ type: 'success', text: t('invoice_view_peppol_success') });
      // Refresh invoice data
      const invoiceData = await invoiceService.getInvoiceById(invoice.Id);
      setInvoice(invoiceData);
    } catch (error) {
      setGeneralMessage({ type: 'error', text: error.message || t('invoice_view_peppol_failed') });
    } finally {
      setLoading(false);
      setSendingToPeppol(false);
      handleClosePeppolDialog();
    }
  };
  
  // Email sending handler - Blocked from executing API calls
  const handleSendByEmail = async () => {
    // ⛔ FEATURE DISABLED: Prevent any email sending
    setGeneralMessage({ type: 'info', text: t('invoice_view_email_disabled_notification') });
    return;
    // The rest of the code is unreachable - protects against browser inspect tampering
  };
  
  const handleCloseEmailDialog = () => {
    setOpenEmailDialog(false);
  };
  
  const handleConfirmSendByEmail = async () => {
    // ⛔ FEATURE DISABLED: Prevent any email sending
    setGeneralMessage({ type: 'info', text: t('invoice_view_email_disabled_notification') });
    return;
    // The rest of the code is unreachable - protects against browser inspect tampering
  };
  
  // PDF Preview handler — renders in dialog
  const handlePreviewInvoicePdf = async () => {
    setPdfPreviewLoading(true);
    try {
      const html = await renderInvoiceTemplate(invoice, storeInfo, t);
      setPdfPreviewHtml(html);
      setPdfPreviewOpen(true);
    } catch (error) {
      setGeneralMessage({ type: 'error', text: t('invoice_view_pdf_preview_failed') });
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  const handleClosePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPdfPreviewHtml('');
  };

  const handleOpenPaymentDialog = () => {
    const remaining = dueAmount;
    setPaymentAmount(remaining.toFixed(2));
    setIsFullPayment(true);
    // Set first available payment method if none selected
    if (!paymentMethod && paymentMethodOptions.length > 0) {
      setPaymentMethod(paymentMethodOptions[0].value);
    }
    setOpenPaymentDialog(true);
  };
  
  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setPaymentAmount('');
    setPaymentMethod(paymentMethodOptions.length > 0 ? paymentMethodOptions[0].value : '');
    setPaymentNotes('');
    setIsFullPayment(false);
  };
  
  const handlePaymentAmountChange = (e) => {
    const value = e.target.value;
    setPaymentAmount(value);
    const amount = parseFloat(value) || 0;
    setIsFullPayment(amount >= dueAmount);
  };
  
  const handleRecordPayment = async () => {
    const amount = parseFloat(paymentAmount);
    
    if (!amount || amount <= 0) {
      setGeneralMessage({ type: 'error', text: t('invalid_payment_amount') || 'Please enter a valid payment amount' });
      return;
    }
    
    if (amount > dueAmount) {
      setGeneralMessage({ type: 'error', text: t('payment_exceeds_due') || 'Payment amount cannot exceed due amount' });
      return;
    }
    
    if (!paymentMethod) {
      setGeneralMessage({ type: 'error', text: t('select_payment_method') || 'Please select a payment method' });
      return;
    }
    
    // Validate payment methods are loaded
    if (!paymentMethodOptions || paymentMethodOptions.length === 0) {
      setGeneralMessage({ type: 'error', text: t('payment_methods_not_loaded') || 'Payment methods not loaded. Please refresh the page.' });
      return;
    }
    
    try {
      setProcessingPayment(true);
      
      const selectedMethod = paymentMethodOptions.find(opt => opt.value === paymentMethod);
      
      if (!selectedMethod || !selectedMethod.id) {
        setGeneralMessage({ type: 'error', text: t('invalid_payment_method') || 'Invalid payment method selected' });
        setProcessingPayment(false);
        return;
      }
      
      // Match backend model exactly
      const paymentData = {
        InvoiceId: invoice.Id,
        PaymentAmount: amount, // Backend expects PaymentAmount (decimal)
        PaymentMethodId: selectedMethod.id, // Backend expects PaymentMethodId (int) - Required
        PaymentMethod: selectedMethod.label, // Backend expects PaymentMethod (string) - Optional
        Notes: paymentNotes || null // Backend expects Notes (string) - Optional
      };
      
      await invoiceService.recordInvoicePayment(paymentData);
      
      setGeneralMessage({ type: 'success', text: t('payment_recorded_success') || 'Payment recorded successfully' });
      
      // Refresh invoice data
      const invoiceData = await invoiceService.getInvoiceById(invoice.Id);
      setInvoice(invoiceData);
      
      handleClosePaymentDialog();
    } catch (error) {
      setGeneralMessage({ type: 'error', text: error.message || t('payment_record_failed') || 'Failed to record payment' });
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Invoice #{invoice.InvoiceNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('invoice_view_created')} {formatDate(invoiceDate)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/invoices')}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              {t('back')}
            </Button>
            {/* PDF Preview Button */}
            <Button
              variant="outlined"
              color="error"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePreviewInvoicePdf}
              disabled={pdfPreviewLoading}
              sx={{ borderRadius: 2 }}
            >
              {pdfPreviewLoading ? t('invoice_view_generating') : t('invoice_view_preview_pdf')}
            </Button>
            {/* Show Send/Retry to Peppol based on status */}
            {/* Show button if customer has Peppol ID OR if invoice was already sent to Peppol OR if there's a Peppol status */}
            {/* Also show for invoices that are posted even without Peppol ID (to inform user it's disabled) */}
            {(isPosted || invoice.IsSendOnPeppol || peppolStatus) && (
              peppolStatus.toLowerCase() === 'failed' || peppolStatus.toLowerCase() === 'error' ? (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<RefreshIcon />}
                  onClick={handleSendToPeppol}
                  disabled={!customer.CustomerPeppolId}
                  sx={{ borderRadius: 2 }}
                >
                  {t('invoice_view_retry_peppol')}
                </Button>
              ) : (peppolStatus.toLowerCase() === 'not sent' || peppolStatus.toLowerCase() === ' ' ) && isPosted ? (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleSendToPeppol}
                  disabled={!customer.CustomerPeppolId}
                  sx={{ borderRadius: 2 }}
                >
                  {t('invoice_view_send_peppol')}
                </Button>
              ) : null
            )}
            {/* Send Email Button - Visible but disabled */}
            {customer.Email && !invoice.IsEmailSend && (
              <Button
                variant="contained"
                color="info"
                startIcon={<SendIcon />}
                onClick={handleSendByEmail}
                disabled={true}
                sx={{ borderRadius: 2 }}
                title={t('invoice_view_email_disabled_notification')}
              >
                {t('invoice_view_send_email')}
              </Button>
            )}
            {(paymentStatus === PAYMENT_STATUS.NOT_PAID || paymentStatus === PAYMENT_STATUS.PARTIAL_PAID) && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PaymentIcon />}
                onClick={handleOpenPaymentDialog}
                sx={{ borderRadius: 2 }}
              >
                {t('invoice_view_record_payment')}
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              disabled={invoice.IsSendOnPeppol && peppolStatus.toLowerCase() === 'sent'}
              sx={{ borderRadius: 2 }}
              title={invoice.IsSendOnPeppol && peppolStatus.toLowerCase() === 'sent'
                ? (t('cannot_edit_peppol_sent') || t('cannot_edit_peppol_sent'))
                : isDraft ? t('invoice_view_continue_draft_title') : t('invoice_view_edit_invoice')}
            >
              {isDraft ? t('invoice_view_continue_draft') : t('invoice_view_edit')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={invoice.IsSendOnPeppol && peppolStatus.toLowerCase() === 'sent'}
              sx={{ borderRadius: 2 }}
              title={invoice.IsSendOnPeppol && peppolStatus.toLowerCase() === 'sent'
                ? (t('cannot_edit_peppol_sent') || t('cannot_edit_peppol_sent'))
                : t('invoice_view_delete_invoice')}
            >
              {t('invoice_view_delete')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Peppol Lock Alert */}
      {invoice.IsSendOnPeppol && peppolStatus.toLowerCase() === 'sent' && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>
            {`🔒 ${t('peppol_sent_lock_title') || 'Peppol Invoice Sent'}`}
          </AlertTitle>
          {t('peppol_sent_lock_msg') || 'This invoice has been sent via Peppol and cannot be edited or deleted for compliance reasons.'}
        </Alert>
      )}

      {/* Peppol ID Missing Alert */}
      {!customer.CustomerPeppolId && isPosted && (peppolStatus.toLowerCase() === 'not sent' || peppolStatus.toLowerCase() === ' ') && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoOutlinedIcon />}>
          <AlertTitle sx={{ fontWeight: 700 }}>
            {t('peppol_id_missing_title') || 'Peppol ID Missing'}
          </AlertTitle>
          {t('peppol_id_missing_message') || 'Peppol ID is missing for this customer. Please add it by editing this customer.'}
        </Alert>
      )}

      {/* General Message Alert */}
      {generalMessage && (
        <Alert 
          severity={generalMessage.type} 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setGeneralMessage(null)}
        >
          {generalMessage.text}
        </Alert>
      )}

      {/* Payment Status Alert */}
      {paymentStatus === PAYMENT_STATUS.PAID && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>{t('invoice_view_fully_paid_title')}</AlertTitle>
          {t('invoice_view_fully_paid_message')}
        </Alert>
      )}

      {paymentStatus === PAYMENT_STATUS.PARTIAL_PAID && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>{t('invoice_view_partial_paid_title')}</AlertTitle>
          {t('invoice_view_partial_paid_message')} €{dueAmount.toFixed(2)}
        </Alert>
      )}

      {paymentStatus === PAYMENT_STATUS.NOT_PAID && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>{t('invoice_view_not_paid_title')}</AlertTitle>
          {t('invoice_view_not_paid_message')} €{dueAmount.toFixed(2)}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Company Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                {t('invoice_view_company_info')}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_name')}</Typography>
                <Typography variant="body1" fontWeight={500}>{storeInfo?.name || t('invoice_view_na')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_vat_number')}</Typography>
                <Typography variant="body1">{storeInfo?.vat || t('invoice_view_na')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_email')}</Typography>
                <Typography variant="body1">{storeInfo?.email || t('invoice_view_na')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_phone')}</Typography>
                <Typography variant="body1">{storeInfo?.phone || t('invoice_view_na')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_address')}</Typography>
                <Typography variant="body1">
                  {storeInfo?.address || t('invoice_view_na')}
                  {storeInfo?.postCode && `, ${storeInfo.postCode}`}
                  {storeInfo?.city && ` ${storeInfo.city}`}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_iban')}</Typography>
                <Typography variant="body1">{storeInfo?.iban || t('invoice_view_na')}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Customer Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ color: '#667eea', mr: 1 }} />
                <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                  {t('invoice_view_customer_info')}
                </Typography>
              </Box>
              <Tooltip title={t('edit_customer') || 'Edit Customer'}>
                <IconButton
                  size="small"
                  onClick={() => setOpenEditCustomerDialog(true)}
                  sx={{
                    color: '#667eea',
                    '&:hover': {
                      bgcolor: 'rgba(102, 126, 234, 0.1)'
                    }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_name')}</Typography>
                <Typography variant="body1" fontWeight={500}>{customerName}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_vat_number')}</Typography>
                <Typography variant="body1">{customerVAT}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_email')}</Typography>
                <Typography variant="body1">{customerEmail}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_phone')}</Typography>
                <Typography variant="body1">{customerPhone}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_address')}</Typography>
                <Typography variant="body1">
                  {customerAddress}
                  {customerPostCode && `, ${customerPostCode}`}
                  {customerCity && ` ${customerCity}`}
                  {customerCountry && `, ${customerCountry}`}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Invoice Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              {t('invoice_summary')}
            </Typography>
            <Grid container spacing={2}>
              {/* Invoice Number */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_invoice_number')}</Typography>
                <Typography variant="body1" fontWeight={500}>{invoice.InvoiceNumber}</Typography>
              </Grid>
              
              {/* Dates */}
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_invoice_date')}</Typography>
                <Typography variant="body2">{formatDate(invoiceDate)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_due_date')}</Typography>
                <Typography variant="body2">{formatDate(invoice.PaymentDueDate || invoice.DueDate)}</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
              
              {/* Status Section */}
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {t('invoice_status') || 'Invoice Status'}
                </Typography>
                <Chip
                  label={invoice.InvoiceStatus || 'Draft'}
                  color={isDraft ? 'default' : 'success'}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {t('peppol_status') || 'Peppol'}
                </Typography>
                <Chip
                  label={peppolStatus || 'Not Sent'}
                  color={
                    peppolStatus.toLowerCase() === 'sent' ? 'success' : 
                    peppolStatus.toLowerCase() === 'failed' || peppolStatus.toLowerCase() === 'error' ? 'error' : 
                    'default'
                  }
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {t('payment_status')}
                </Typography>
                <Chip
                  label={paymentStatus}
                  color={paymentColor}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
              
              {/* Financial Summary */}
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_subtotal_excl')}</Typography>
                <Typography variant="h6" fontWeight={600}>€{totalExcl.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_vat')} ({vatPercentage}%)</Typography>
                <Typography variant="h6" fontWeight={600}>€{totalVAT.toFixed(2)}</Typography>
              </Grid>
              
              {/* TVA Included Status */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('price_type') || 'Price Type'}</Typography>
                <Chip
                  label={invoice.IsTvaIncluded ? (t('total_incl_vat') || 'Incl. VAT') : (t('total_excl_vat') || 'Excl. VAT')}
                  color={invoice.IsTvaIncluded ? 'success' : 'default'}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                />
              </Grid>
              
              {/* Discount Summary */}
              {discountAmount > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('discount')} {discountType.toLowerCase() === 'percentage' ? `(${discountValue}%)` : ''}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#ff9800' }}>
                    -€{discountAmount.toFixed(2)}
                  </Typography>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_total_incl')}</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#667eea' }}>
                  €{totalIncl.toFixed(2)}
                </Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_paid_amount')}</Typography>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#4caf50' }}>
                  €{paidAmount.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_due_amount')}</Typography>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#f44336' }}>
                  €{dueAmount.toFixed(2)}
                </Typography>
              </Grid>
              
              {/* Payment Methods Section */}
              {aggregatedPayments.length > 0 ? (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('invoice_view_payment_methods')}
                    </Typography>
                    {aggregatedPayments.map((payment, index) => (
                      <Box 
                        key={index}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          py: 0.5,
                          borderBottom: index < aggregatedPayments.length - 1 ? '1px solid #f0f0f0' : 'none'
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {payment.methodName}:
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          €{payment.totalAmount.toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Grid>
                </>
              ) : invoice.PaymentMethod && paidAmount > 0 && !invoice.PaymentMethod.includes('/') ? (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_payment_method')}</Typography>
                    <Chip 
                      label={invoice.PaymentMethod} 
                      size="small" 
                      color="primary"
                      sx={{ mt: 1, fontWeight: 600 }}
                    />
                  </Grid>
                </>
              ) : null}
            </Grid>
          </Paper>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12} sx={{ mt: 4 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              {t('invoice_view_line_items')}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                    <TableCell sx={{ fontWeight: 600 }}>{t('invoice_view_product')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('invoice_view_sku_id')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">{t('invoice_view_quantity')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('invoice_view_unit_price')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">{t('invoice_view_vat_percent')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('invoice_view_total_excl')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('invoice_view_total_incl') || 'Total Incl.'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.length > 0 ? (
                    lineItems.map((item, index) => {
                      const itemTotalExcl = Number(item.TotalExcl ?? 0);
                      const itemVatAmount = Number(item.VATAmount ?? item.VatAmount ?? item.TotalVAT ?? 0);
                      // Backend omits TotalIncl, so sum the two stored amounts instead of re-applying the VAT rate.
                      const itemTotalIncl = Number(item.TotalIncl ?? item.TotalInclVAT ?? itemTotalExcl + itemVatAmount);
                      const itemVatPercentage = Number(item.VATPercentage ?? item.VAT ?? vatPercentage);
                      
                      return (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography fontWeight={500}>{item.DisplayName || item.ProductName || item.Name || t('invoice_view_na')}</Typography>
                          {item.Description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {item.Description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.Reference || item.ProductId ? (
                            <Chip 
                              label={item.Reference || item.ProductId} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ) : t('invoice_view_na')}
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={item.Quantity || 0} 
                            size="small" 
                            color="info"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={500}>€{Number(item.UnitPriceExclTVA || item.UnitPrice || item.Price || 0).toFixed(2)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={`${itemVatPercentage}%`} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#667eea' }}>
                          €{itemTotalExcl.toFixed(2)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#4caf50' }}>
                          €{itemTotalIncl.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )})
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ color: '#888', py: 4 }}>
                        {t('invoice_view_no_line_items')}
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Totals Row */}
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={6} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      {t('invoice_view_subtotal_excl')}:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{totalExcl.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  {/* Discount Row */}
                  {discountAmount > 0 && (
                    <TableRow sx={{ bgcolor: 'rgba(255, 152, 0, 0.08)' }}>
                      <TableCell colSpan={6} align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#ff9800' }}>
                        {t('discount')} {discountType.toLowerCase() === 'percentage' ? `(${discountValue}%)` : ''}:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#ff9800' }}>
                        -€{discountAmount.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={6} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      {t('invoice_view_vat')} ({vatPercentage}%):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{totalVAT.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                    <TableCell colSpan={6} align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      {t('invoice_view_total_incl')}:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#667eea' }}>
                      €{totalIncl.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Payment History Section */}
        {aggregatedPayments.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon sx={{ color: '#667eea', mr: 1 }} />
                <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                  {t('invoice_view_payment_history')}
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{t('invoice_view_payment_method')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{t('invoice_view_amount_paid')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">{t('invoice_view_payment_date')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('invoice_view_notes')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.Payments && invoice.Payments.map((payment, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip 
                            label={payment.PaymentMethodName || payment.PaymentMethod?.Name || t('invoice_view_unknown_method')}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" fontWeight={600} sx={{ color: '#4caf50' }}>
                            €{Number(payment.Amount || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {payment.PaymentDate ? formatDate(payment.PaymentDate) : formatDate(payment.CreatedDate || invoice.CreatedDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {payment.Notes || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        {t('invoice_view_total_paid')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#4caf50' }}>
                        €{paidAmount.toFixed(2)}
                      </TableCell>
                      <TableCell colSpan={2}>
                        {dueAmount > 0 ? (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('invoice_view_remaining_due')}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#f44336' }}>
                              €{dueAmount.toFixed(2)}
                            </Typography>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              startIcon={<PaymentIcon />}
                              onClick={handleOpenPaymentDialog}
                              sx={{ 
                                borderRadius: 2,
                                ml: 2,
                                fontWeight: 600
                              }}
                            >
                              {t('invoice_view_record_payment')}
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                            <Chip
                              label={t('invoice_view_fully_paid_label')}
                              color="success"
                              icon={<PaymentIcon />}
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* Peppol Information if sent */}
        {invoice.IsSendOnPeppol && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
                {t('invoice_view_peppol_info')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">{t('status')}</Typography>
                  <Chip 
                    label={invoice.PeppolStatus || t('invoice_view_not_sent')} 
                    color={invoice.PeppolStatus === 'Sent' ? 'success' : 'default'}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_message_id')}</Typography>
                  <Typography variant="body2">{invoice.PeppolMessageId || t('invoice_view_na')}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">{t('invoice_view_sent_date')}</Typography>
                  <Typography variant="body2">{invoice.PeppolSentDate ? formatDate(invoice.PeppolSentDate) : t('invoice_view_na')}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Custom Notes if available */}
        {invoice.CustomText && invoice.CustomText.trim() !== '' && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
                {t('invoice_view_additional_notes')}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {invoice.CustomText}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
      
      {/* Payment Dialog */}
      <Dialog 
        open={openPaymentDialog} 
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PaymentIcon sx={{ mr: 1 }} />
            {t('invoice_view_payment_dialog_title')}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">{t('invoice_view_total_amount')}</Typography>
                <Typography variant="h6" fontWeight={600}>€{totalIncl.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">{t('invoice_view_amount_due')}</Typography>
                <Typography variant="h6" fontWeight={600} color="error">€{dueAmount.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">{t('invoice_view_already_paid')}</Typography>
                <Typography variant="h6" fontWeight={600} color="success.main">€{paidAmount.toFixed(2)}</Typography>
              </Grid>
            </Grid>
          </Box>
          
          <TextField
            fullWidth
            label={t('payment_amount')}
            type="number"
            value={paymentAmount}
            onChange={handlePaymentAmountChange}
            inputProps={{ min: 0, step: 0.01, max: dueAmount }}
            sx={{ mb: 2 }}
            helperText={isFullPayment ? t('invoice_view_full_payment') : t('invoice_view_partial_payment')}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('invoice_view_payment_method_required')}</InputLabel>
            <Select
              value={paymentMethod}
              label={t('invoice_view_payment_method_required')}
              onChange={(e) => setPaymentMethod(e.target.value)}
              required
              disabled={paymentMethodOptions.length === 0}
            >
              {paymentMethodOptions.length > 0 ? (
                paymentMethodOptions.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  {t('invoice_view_no_payment_methods')}
                </MenuItem>
              )}
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label={t('invoice_view_notes_optional')}
            multiline
            rows={3}
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.target.value)}
            inputProps={{ maxLength: 500 }}
            sx={{ mb: 2 }}
            helperText={`${paymentNotes.length}/500 ${t('invoice_view_characters')}`}
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            {isFullPayment 
              ? t('invoice_view_full_payment_info') 
              : t('invoice_view_partial_payment_info')}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClosePaymentDialog}
            disabled={processingPayment}
          >
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleRecordPayment}
            variant="contained"
            color="primary"
            disabled={processingPayment || !paymentAmount || parseFloat(paymentAmount) <= 0 || !paymentMethod || paymentMethodOptions.length === 0}
            sx={{ minWidth: 120 }}
          >
            {processingPayment ? t('invoice_view_processing') : t('invoice_view_record_payment')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Peppol Send/Retry Dialog */}
      <Dialog 
        open={openPeppolDialog} 
        onClose={handleClosePeppolDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SendIcon sx={{ mr: 1 }} />
            {invoice.PeppolStatus?.toLowerCase() === 'failed' || invoice.PeppolStatus?.toLowerCase() === 'error' 
              ? t('invoice_view_peppol_dialog_retry') 
              : t('invoice_view_peppol_dialog_send')}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* Invoice Preview */}
          {loadingPreview ? (
            <Box 
              sx={{ 
                mb: 3, 
                p: 6, 
                textAlign: 'center', 
                bgcolor: '#f5f5f5', 
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <CircularProgress size={50} />
              <Typography variant="body1" color="text.secondary">
                {t('invoice_view_preparing_preview')}
              </Typography>
            </Box>
          ) : peppolPreviewHtml ? (
            <Box 
              sx={{ 
                mb: 3, 
                maxHeight: '700px', 
                overflowY: 'auto', 
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                bgcolor: 'white'
              }}
            >
              <div 
                dangerouslySetInnerHTML={{ __html: peppolPreviewHtml }}
                style={{ 
                  transform: 'scale(0.95)', 
                  transformOrigin: 'top left',
                  width: '105.26%',
                  padding: '20px'
                }}
              />
            </Box>
          ) : (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('invoice_view_invoice_details')}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                #{invoice.InvoiceNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Customer: {customerName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Amount: €{totalIncl.toFixed(2)}
              </Typography>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={peppolSendOnPeppol}
                onChange={(e) => setPeppolSendOnPeppol(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {t('invoice_view_peppol_network')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('invoice_view_peppol_network_desc')}
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={peppolAttachPdf}
                onChange={(e) => setPeppolAttachPdf(e.target.checked)}
                color="primary"
                disabled={!peppolSendOnPeppol}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {t('invoice_view_attach_pdf')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('invoice_view_attach_pdf_desc')}
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start' }}
          />

          {!peppolSendOnPeppol && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('invoice_view_peppol_confirm_warning')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClosePeppolDialog} disabled={sendingToPeppol}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleConfirmSendToPeppol}
            variant="contained"
            disabled={sendingToPeppol || !peppolSendOnPeppol}
            sx={{ 
              minWidth: 120,
              bgcolor: '#667eea',
              '&:hover': { bgcolor: '#5568d3' }
            }}
          >
            {sendingToPeppol ? t('invoice_view_sending') : t('invoice_view_confirm_send')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Send Dialog - Visible but functionality disabled */}
      <Dialog 
        open={openEmailDialog} 
        onClose={handleCloseEmailDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#2196f3', color: 'white', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SendIcon sx={{ mr: 1 }} />
            {t('invoice_view_email_dialog_title')}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {loadingEmailPreview ? (
            <Box 
              sx={{ 
                p: 6, 
                textAlign: 'center', 
                bgcolor: '#f5f5f5', 
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <CircularProgress size={50} />
              <Typography variant="body1" color="text.secondary">
                {t('invoice_view_preparing_preview')}
              </Typography>
            </Box>
          ) : emailPreviewHtml ? (
            <Box 
              sx={{ 
                maxHeight: '500px', 
                overflowY: 'auto', 
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                bgcolor: 'white'
              }}
            >
              <div 
                dangerouslySetInnerHTML={{ __html: emailPreviewHtml }}
                style={{ 
                  transform: 'scale(0.95)', 
                  transformOrigin: 'top left',
                  width: '105.26%',
                  padding: '20px'
                }}
              />
            </Box>
          ) : (
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>{t('invoice_view_email_disabled_title')}</AlertTitle>
                {t('invoice_view_email_disabled_message')}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('invoice_view_invoice_details')}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                #{invoice.InvoiceNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Customer: {customerName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Amount: €{totalIncl.toFixed(2)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9', flex: 1, mr: 2 }}>
            <SendIcon color="info" fontSize="small" />
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('invoice_view_send_to')}
              </Typography>
              <Typography variant="body2" fontWeight={600} color="info.main">
                {customer.Email}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCloseEmailDialog} disabled={sendingEmail}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleConfirmSendByEmail}
              variant="contained"
              disabled={true}
              title={t('invoice_view_email_disabled_notification')}
              sx={{ 
                minWidth: 120,
                bgcolor: '#2196f3',
                '&:hover': { bgcolor: '#1976d2' }
              }}
            >
              {t('invoice_view_confirm_send')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* PDF Preview Dialog with Print & Download */}
      <InvoicePreviewDialog
        open={pdfPreviewOpen}
        onClose={handleClosePdfPreview}
        html={pdfPreviewHtml}
        documentTitle={`invoice-${invoice?.InvoiceNumber || 'preview'}`}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('confirm_delete') || 'Confirm Delete'}
        message={
          (invoice.SaleOrderId || invoice.SaleOrderNumber || invoice.FromSaleOrder)
            ? (t('invoice_delete_sale_order_info') || 
               'Are you sure you want to delete this invoice? Note: This invoice was converted from a sale order. Deleting this invoice will not affect stock inventory as the sale order record will remain.')
            : (t('invoice_delete_confirm') || 'Are you sure you want to delete this invoice? This action cannot be undone.')
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        confirmText={t('delete') || 'Delete'}
        cancelText={t('cancel') || 'Cancel'}
        variant="danger"
      />

      {/* Edit Customer Dialog */}
      <AddCustomerDialog
        open={openEditCustomerDialog}
        onClose={() => setOpenEditCustomerDialog(false)}
        editMode={true}
        initialData={customer}
        onCustomerUpdated={async (updatedCustomer) => {
          setGeneralMessage({ type: 'success', text: t('customer_updated_success') || 'Customer updated successfully!' });
          // Refresh invoice data to get updated customer information
          try {
            const invoiceData = await invoiceService.getInvoiceById(invoice.Id);
            setInvoice(invoiceData);
          } catch (error) {
            // Silent fail - customer was updated but invoice refresh failed
          }
        }}
      />
    </Container>
  );
};

export default InvoiceViewPage;
